<!-- Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. -->
<form [formGroup]="formGroup" class="reports-form" *ngIf="activeControl$ | async as activeControl">
	<title-header [title]="strings.REPORT_TITLE" (closeButtonClicked)="close()"></title-header>

	<div>
		<!-- Report Type -->
		<mat-extended-select
			formControlName="reportType"
			placeholder="{{ strings.REPORT_TYPE }}"
			automationId="reportPanelType"
			[includeFilter]="false"
			[option$]="reportTypeOption$"
			[valuePath]="['type']"
			[sortCompareFunction]="sortReportTypeByOrdinal"
		>
		</mat-extended-select>
		<!-- Format Type dropdown (and optional P6 export controls) -->
		<mat-extended-select
			*ngIf="activeControl['exportFormatType']"
			formControlName="exportFormatType"
			automationId="reportPanelExportFormatType"
			class="filter-defaults-grid-span exportFormatType"
			placeholder="{{ strings.REPORT_EXPORT_TICKETS_FORMAT }}"
			[includeFilter]="false"
			[option$]="exportFormatTypeOptions$"
			[sortCompareFunction]="compareExportFormatsForSorting"
			[valuePath]="['type']"
		>
		</mat-extended-select>

		<mat-form-field class="file-upload" *ngIf="activeControl['exportFormatTemplate']">
			<mat-label>{{ strings.REPORT_EXPORT_TEMPLATE_FORMAT_PLACEHOLDER }}</mat-label>
			<tp-file-upload
				fileUploadSingle
				#fileUploadController
				formControlName="exportFormatTemplate"
				automationId="reportPanelExportFormatTemplate"
				class="file-upload"
				[buttonText]="strings.REPORT_EXPORT_TEMPLATE_FORMAT_BUTTON"
				[outputFormat]="UploadOutputFormat.File"
				[acceptedFileTypes]="p6SupportedFormats"
			>
			</tp-file-upload>
			<mat-error *ngIf="formGroup?.get('exportFormatTemplate')?.hasError('required')">{{
				strings.REPORT_ERROR_REQUIRED
			}}</mat-error>
			<mat-error *ngIf="formGroup?.get('exportFormatTemplate')?.hasError('dataFormatIncorrect')">
				{{ strings.REPORT_EXPORT_TEMPLATE_INVALID_FORMAT }}
			</mat-error>
		</mat-form-field>
	</div>

	<div class="reports-container">
		<!-- Scrolled content -->

		<mat-accordion
			*ngIf="currentReportType !== 'exportToCSV' && currentReportType !== 'onDeckTickets'"
			multi="true"
			class="expansion-panels"
			automationId="reportPanelExpansionPanels"
		>
			<!-- FILTERS: Expansion Panel -->
			<mat-expansion-panel [expanded]="true" class="expansion-panel">
				<mat-expansion-panel-header>
					<mat-panel-title automationId="reportPanelFilters">{{ strings.REPORT_FILTERS }}</mat-panel-title>
				</mat-expansion-panel-header>

				<!-- FILTERS: Controls -->
				<div class="filter-controls-grid {{ currentReportType }}">
					<div class="date-filter-controls-grid">
						<mat-form-field *ngIf="activeControl['asOfDate']" class="date-filter-field">
							<mat-label>{{ strings.UICONTROL_TEXT_START_DATE }}</mat-label>
							<input
								matInput
								readonly
								[matDatepicker]="asOfDatePicker"
								automationId="reportPanelDateAdapter"
								formControlName="asOfDate"
								(click)="asOfDatePicker.open()"
							/>
							<mat-error
								*ngIf="formGroup.get('asOfDate')?.errors?.required"
								automationId="reportErrorRequiredStartDate"
								>{{ strings.REPORT_ERROR_REQUIRED }}</mat-error
							>
							<mat-datepicker-toggle
								automationId="asOfDatePicker"
								matSuffix
								[for]="asOfDatePicker"
							></mat-datepicker-toggle>
							<mat-datepicker touchUi #asOfDatePicker></mat-datepicker>
						</mat-form-field>

						<mat-form-field *ngIf="activeControl['endDate']" class="date-filter-field">
							<mat-label>{{ strings.UICONTROL_TEXT_END_DATE }}</mat-label>
							<input
								matInput
								readonly
								[matDatepicker]="endDatePicker"
								automationId="reportPanelEndDatePicker"
								formControlName="endDate"
								(click)="endDatePicker.open()"
							/>
							<mat-error
								*ngIf="formGroup.get('endDate')?.errors?.required"
								automationId="reportErrorRequiredEndDate"
								>{{ strings.REPORT_ERROR_REQUIRED }}</mat-error
							>
							<mat-datepicker-toggle
								automationId="endDatePicker"
								matSuffix
								[for]="endDatePicker"
							></mat-datepicker-toggle>
							<mat-datepicker touchUi #endDatePicker></mat-datepicker>
						</mat-form-field>
					</div>
					<mat-error
						*ngIf="formGroup.errors?.invalidDateRange"
						class="filter-defaults-grid-span"
						automationId="reportErrorStartEndDate"
						>{{ strings.REPORT_ERROR_START_END_DATE }}</mat-error
					>

					<div class="date-filter-controls-grid">
						<mat-extended-select
							*ngIf="activeControl['numberOfWeeks']"
							formControlName="numberOfWeeks"
							automationId="reportPanelNumberOfWeeks"
							class="date-filter-field"
							placeholder="{{ strings.REPORT_NUMBER_OF_WEEKS }}"
							[includeFilter]="false"
							[option$]="numberOfWeekOption$"
						>
						</mat-extended-select>
					</div>

					<mat-extended-select
						*ngIf="activeControl['plans']"
						formControlName="plans"
						automationId="reportPanelPlans"
						class="filter-defaults-grid-span plans"
						placeholder="{{ strings.REPORT_PLANS }}"
						[required]="false"
						[includeFilter]="true"
						[multiSelect]="true"
						[noSelectionText]="strings.REPORT_ALL_PLANS"
						[option$]="planOption$"
						[labelPath]="['view', 'name']"
						[valuePath]="['$id']"
					>
					</mat-extended-select>

					<mat-extended-select
						*ngIf="activeControl['locations']"
						formControlName="locations"
						automationId="reportPanelLocations"
						class="filter-defaults-grid-span locations"
						placeholder="{{ strings.PROJECT_ACTION_LOCATIONS }}"
						[required]="false"
						[includeFilter]="true"
						[multiSelect]="true"
						[noSelectionText]="strings.REPORT_ALL_LOCATIONS"
						[option$]="locationOption$"
						[labelPath]="['view', 'name']"
						[valuePath]="['$id']"
						[iconBackgroundColorPath]="['view', 'color']"
					>
					</mat-extended-select>

					<mat-extended-select
						*ngIf="activeControl['swimlanes']"
						formControlName="swimlanes"
						automationId="reportPanelSwimlanes"
						class="filter-defaults-grid-span"
						placeholder="{{ strings.PROJECT_ACTION_SWIMLANES }}"
						[required]="false"
						[includeFilter]="true"
						[multiSelect]="true"
						[noSelectionText]="strings.REPORT_ALL_SWIMLANES"
						[option$]="collapsedSwimlanesDropdownOptions$"
						[labelPath]="['label']"
						[valuePath]="['key']"
						[iconBackgroundColorPath]="['color']"
					/>

					<mat-extended-select
						*ngIf="activeControl['roles']"
						formControlName="roles"
						automationId="reportPanelRoles"
						class="filter-defaults-grid-span roles"
						placeholder="{{ strings.PROJECT_ACTION_ROLES }}"
						[required]="false"
						[includeFilter]="true"
						[multiSelect]="true"
						[noSelectionText]="strings.REPORT_ALL_ROLES"
						[option$]="roleOption$"
						[labelPath]="['view', 'roleName']"
						[valuePath]="['$id']"
						[iconBackgroundColorPath]="['view', 'color']"
					>
					</mat-extended-select>

					<mat-extended-select
						*ngIf="activeControl['ticketTypes']"
						formControlName="ticketTypes"
						automationId="reportPanelTicketTypes"
						class="filter-defaults-grid-span ticketTypes"
						placeholder="{{ strings.CUSTOM_FIELD_INFO_TICKET_TYPES }}"
						[includeFilter]="false"
						[required]="false"
						[multiSelect]="true"
						[noSelectionText]="strings.REPORT_ALL_TICKET_TYPES"
						[option$]="ticketTypeOptions$"
						[valuePath]="[]"
					>
					</mat-extended-select>

					<mat-extended-select
						*ngIf="activeControl['externalLinkage']"
						formControlName="externalLinkage"
						automationId="reportPanelExternalLinkage"
						class="filter-defaults-grid-span externalLinkage"
						placeholder="{{ strings.FILTER_BY_EXTERNAL_SCHEDULE_ID }}"
						[includeFilter]="false"
						[option$]="externalLinkageOptions$"
						[valuePath]="[]"
					>
					</mat-extended-select>

					<!-- ARCHIVED PLANS DROPDOWN -->
					<ng-container *ngIf="activeControl['archivedPlansDropDown']">
						<span class="filter-defaults-grid-span archived-plans-warning">{{
							strings.REPORT_INCLUDE_ARCHIVE_PLANS_INCREASE_GENERATION_TIME
						}}</span>

						<mat-extended-select
							formControlName="archivedPlansDropDown"
							automationId="reportPanelArchivedPlans"
							class="filter-defaults-grid-span"
							[placeholder]="strings.REPORT_ARCHIVED_PLANS"
							[required]="false"
							[includeFilter]="true"
							[multiSelect]="true"
							[option$]="archivedPlanOption$"
							[labelPath]="['view', 'name']"
							[valuePath]="['$id']"
							[showSelectAllCheckbox]="false"
							(closed)="onArchivedPlansMultiselectClosed()"
						>
						</mat-extended-select>
					</ng-container>

					<mat-slide-toggle
						*ngIf="activeControl['includeWBSCodeFilterToggle']"
						class="toggle-defaults-grid-span"
						automationId="reportPanelIncludeWBSCodeFilterToggle"
						formControlName="includeWBSCodeFilterToggle"
					>
						{{ strings.REPORT_INCLUDE_WBS_CODE_TOGGLES_TEXT }}
					</mat-slide-toggle>
					<mat-form-field
						*ngIf="activeControl['wbsCodeFilterConfig']"
						class="filter-defaults-grid-span wbsCodeFilterConfig"
					>
						<mat-label>{{ strings.WBS_CODE }}</mat-label>
						<tp-filter
							formControlName="wbsCodeFilterConfig"
							automationId="reportPanelWbsCodeFilterConfig"
							fieldType="text"
						>
						</tp-filter>
					</mat-form-field>

					<mat-slide-toggle
						*ngIf="activeControl['includePullTickets']"
						class="toggle-defaults-grid-span"
						formControlName="includePullTickets"
						automationId="reportPanelIncludePullTickets"
					>
						{{ strings.REPORT_INCLUDE_PULL_TICKETS }}
					</mat-slide-toggle>

					<mat-slide-toggle
						*ngIf="activeControl['includeCompleted']"
						class="toggle-defaults-grid-span"
						formControlName="includeCompleted"
						automationId="reportPanelIncludeCompleted"
					>
						{{ strings.REPORT_INCLUDE_COMPLETED_TICKETS }}
					</mat-slide-toggle>

					<mat-slide-toggle
						*ngIf="activeControl['onlyLate']"
						class="toggle-defaults-grid-span"
						formControlName="onlyLate"
						automationId="reportPanelOnlyLate"
					>
						{{ strings.REPORT_ONLY_LATE }}
					</mat-slide-toggle>

					<mat-slide-toggle
						*ngIf="activeControl['filterByCustomFieldsToggle'] && customFieldDefinitionsSync.size > 0"
						class="toggle-defaults-grid-span"
						formControlName="filterByCustomFieldsToggle"
						automationId="reportPanelFilterByCustomFields"
					>
						{{ strings.REPORT_FILTER_BY_CUSTOM_FIELDS }}
					</mat-slide-toggle>

					<ng-container *ngIf="customFieldDefinitionsSync.size > 0">
						<custom-field-filter
							class="filter-defaults-grid-span"
							automationId="customField1"
							*ngIf="activeControl['customField1']"
							[title]="strings.FILTER_CUSTOM_FIELD_FILTER_1"
							[customFieldDefinitions]="customFieldDefinitions"
							formControlName="customField1"
						></custom-field-filter>
						<mat-error
							class="filter-defaults-grid-span"
							*ngIf="formGroup.get('customField1')?.errors?.filterIncomplete"
							>{{ strings.REPORT_ERROR_CUSTOMFIELD }}</mat-error
						>
						<custom-field-filter
							class="filter-defaults-grid-span"
							automationId="customField2"
							*ngIf="activeControl['customField2']"
							[title]="strings.FILTER_CUSTOM_FIELD_FILTER_2"
							[customFieldDefinitions]="customFieldDefinitions"
							formControlName="customField2"
						></custom-field-filter>
						<mat-error
							class="filter-defaults-grid-span"
							*ngIf="formGroup.get('customField2')?.errors?.filterIncomplete"
							>{{ strings.REPORT_ERROR_CUSTOMFIELD }}</mat-error
						>
						<custom-field-filter
							class="filter-defaults-grid-span"
							automationId="customField3"
							*ngIf="activeControl['customField3']"
							[title]="strings.FILTER_CUSTOM_FIELD_FILTER_3"
							[customFieldDefinitions]="customFieldDefinitions"
							formControlName="customField3"
						></custom-field-filter>
						<mat-error
							class="filter-defaults-grid-span"
							*ngIf="formGroup.get('customField3')?.errors?.filterIncomplete"
							>{{ strings.REPORT_ERROR_CUSTOMFIELD }}</mat-error
						>
					</ng-container>
				</div>
			</mat-expansion-panel>

			<!-- LAYOUT: Expansion Panel -->
			<mat-expansion-panel *ngIf="thereAreLayoutControl$ | async" [expanded]="true" class="expansion-panel">
				<mat-expansion-panel-header>
					<mat-panel-title automationId="reportPanelLayout">{{ strings.REPORT_LAYOUT }}</mat-panel-title>
				</mat-expansion-panel-header>

				<!-- LAYOUT: Controls -->
				<div class="layout-controls {{ currentReportType }}">
					<mat-extended-select
						*ngIf="activeControl['customFields']"
						formControlName="customFields"
						automationId="reportPanelCustomFields"
						placeholder="{{ strings.FILTER_BY_CUSTOM_FIELDS }}"
						[required]="false"
						[includeFilter]="true"
						[multiSelect]="true"
						[sortOptions]="false"
						[option$]="customFields$"
						[labelPath]="['view', 'label']"
						[valuePath]="['$id']"
					/>

					<mat-extended-select
						*ngIf="activeControl['ppcFormat']"
						formControlName="ppcFormat"
						placeholder="PPC format"
						[sortCompareFunction]="sortPpcFormatByOrdinal"
						automationId="reportPanelPPCFormat"
						[includeFilter]="false"
						[required]="true"
						[option$]="ppcFormatOptions$"
						[valuePath]="['format']"
					>
					</mat-extended-select>

					<mat-extended-select
						*ngIf="activeControl['timeScale']"
						formControlName="timeScale"
						automationId="reportPanelTimeScale"
						placeholder="{{ strings.REPORT_TIME_SCALE }}"
						[includeFilter]="false"
						[required]="false"
						[option$]="timeScaleOption$"
						[sortCompareFunction]="timeScaleMenuSorting"
						[valuePath]="[]"
					>
					</mat-extended-select>

					<mat-extended-select
						*ngIf="activeControl['sortBy']"
						formControlName="sortBy"
						automationId="reportPanelSortBy"
						placeholder="{{ strings.REPORT_SORT_BY }}"
						[includeFilter]="false"
						[required]="false"
						[option$]="sortByOption$"
						[sortCompareFunction]="alphaSortButWithOffFirst"
						[valuePath]="[]"
					>
					</mat-extended-select>

					<mat-extended-select
						*ngIf="activeControl['groupBy']"
						formControlName="groupBy"
						automationId="reportPanelGroupBy"
						placeholder="{{ strings.REPORT_GROUP_BY }}"
						[includeFilter]="false"
						[required]="false"
						[option$]="groupByOption$"
						[sortCompareFunction]="alphaSortButWithOffFirst"
						[valuePath]="[]"
					>
					</mat-extended-select>

					<mat-extended-select
						*ngIf="activeControl['wbsGroupingLevel']"
						formControlName="wbsGroupingLevel"
						[placeholder]="strings.REPORT_WBS_GROUPING_LEVEL_TEXT"
						automationId="wbsGroupingLevel"
						[includeFilter]="false"
						[option$]="wbsLevelList$"
						[sortOptions]="false"
					>
					</mat-extended-select>

					<mat-extended-select
						*ngIf="activeControl['sortGroupsBy']"
						formControlName="sortGroupsBy"
						automationId="reportPanelSortGroupsBy"
						placeholder="{{ strings.REPORT_SORT_GROUPS_BY }}"
						[includeFilter]="false"
						[required]="false"
						[option$]="sortGroupsByOption$"
						[sortCompareFunction]="alphaSortButWithOffFirst"
						[valuePath]="[]"
					>
					</mat-extended-select>

					<mat-extended-select
						*ngIf="activeControl['breakUpBy']"
						formControlName="breakUpBy"
						automationId="reportPanelBreakUpBy"
						placeholder="{{ strings.REPORT_BREAK_UP_BY }}"
						[includeFilter]="false"
						[required]="false"
						[option$]="breakUpByOption$"
						[sortCompareFunction]="alphaSortButWithOffFirst"
						[valuePath]="[]"
					>
					</mat-extended-select>
				</div>
			</mat-expansion-panel>
		</mat-accordion>

		<div class="csv-panel" *ngIf="activeControl['csvExportType']">
			<p *ngIf="isPlanArchiveEnabled$ | async">
				{{ strings.REPORT_INCLUDE_ARCHIVE_PLANS_INCREASE_GENERATION_TIME }}
			</p>
			<mat-radio-group
				class="csv-export-type-layout"
				formControlName="csvExportType"
				*ngIf="activeControl['csvExportType']"
			>
				<mat-radio-button [value]="'project'" automationId="reportPanelProjectCSVRadioBtn"
					>{{ strings.REPORT_CSV_PROJECT_LABEL }}
				</mat-radio-button>
				<mat-radio-button
					*ngIf="isPlanArchiveEnabled$ | async"
					[value]="'projectWithArchivedPlans'"
					automationId="reportPanelProjectWithArchivedPlansCSVRadioBtn"
					>{{ strings.REPORT_CSV_PROJECT_WITH_ARCHIVED_PLANS_LABEL }}
				</mat-radio-button>
				<mat-radio-button [value]="'plan'" automationId="reportPanelPlanCSVRadioBtn"
					>{{ strings.REPORT_CSV_PLAN_LABEL }}
				</mat-radio-button>
			</mat-radio-group>

			<mat-extended-select
				*ngIf="activeControl['csvPlan']"
				formControlName="csvPlan"
				automationId="reportPanelCSVPlan"
				placeholder="{{ strings.REPORT_CSV_PLAN }}"
				[includeFilter]="false"
				[option$]="planOptionForCsvReport$"
				[labelPath]="['view', 'customCSVDropdownLabel']"
				[valuePath]="['$id']"
				style="'padding-left':24px "
			>
			</mat-extended-select>
		</div>

		<report-footer
			class="reportFooter"
			automationId="reportFooter"
			[stateUI$]="stateUI$"
			[reportProgress$]="reportProgress$"
			[disableRunReportButton]="disableRunReportButton$ | async"
			[downloadUrl]="downloadURL"
			(runReport)="runReport()"
			(cancel)="cancel()"
			(viewReport)="viewReport()"
			(downloadCsv)="downloadCsv()"
			(clearSettings)="clearSettings()"
			(editSettings)="editSettings()"
		></report-footer>
	</div>
</form>
